import PBService from "@irati/utils/services"
import client, {QueryOptions} from "@irati/utils/services/apollo"
import getProductStockCheckByPoNumberQuery, {poNumberCountKey} from "@irati/stock/services/queries/PoNumberCountList"
import {DealStockCounts, Stock} from "@irati/types"
import stocksErrorHandler from "./errors/stocksErrorHandler"
import getStockByIdQuery, {stockByIdKey} from "./queries/stockById"
import createStockMutation, {createStockKey} from "./mutations/createStock"
import createStockBulkUpdateMutation, {createStockBulkUploadKey} from "./mutations/createStockBulkUpload"
import stockCount from "./queries/StockQuery"

interface PerkSagasStockCounts extends Omit<DealStockCounts, "stockTotals"> {
  stockTotals: {[key: string]: number}
}

class StockService extends PBService {

  getStockById(stockId: string, options: QueryOptions = {}) {
    const variables = {stockId}
    return client.query({...options, query: getStockByIdQuery, variables}).then(({data}) => data[stockByIdKey])
  }

  createStockBulkUpload(stocks: Stock[], options: QueryOptions = {}): Promise<boolean> {
    return client.mutate({...options, mutation: createStockBulkUpdateMutation, variables: {stocks}})
      .then(({data}) => data[createStockBulkUploadKey])
  }

  createStock(stock: Stock, options: QueryOptions = {}): Promise<boolean> {
    return client.mutate({...options, mutation: createStockMutation, variables: {stock}}).then(({data}) => data[createStockKey])
  }

  getStockForProduct(productId: string, limit: number = 9999, status: string = "OPEN", options: QueryOptions = {}) {
    const variables = {limit, status, productId}
    return client
      .query({...options, query: getProductStockCheckByPoNumberQuery, variables})
      .then(({data}) => data[poNumberCountKey])
  }

  getCurrentStockUsageType(productId: string) {
    return this.getStockForProduct(productId, 1).then((stock) => {
      if (stock[0]) {
        return stock[0].usage
      }
      return null
    })
  }

  stockCount(dealId: string, options: QueryOptions = {}): Promise<PerkSagasStockCounts> {
    const variables = {dealId}

    return client.query({...options, query: stockCount, variables}).then(({data}) => {
      const keyedStock: {[key: string]: number} = {}
      data.dealStockCount.stockTotals.forEach((value: any) => {
        keyedStock[value.id] = value.count
      })
      return {...data.dealStockCount, stockTotals: keyedStock}
    })
  }

}

const service = new StockService({errorHandlers: [stocksErrorHandler]})

export default service

export const getStockById = service.getStockById
export const createStockBulkUpload = service.createStockBulkUpload
export const createStock = service.createStock
export const getStockForProduct = service.getStockForProduct
export const getCurrentStockUsageType = service.getCurrentStockUsageType
